/**
 * Copy resolver for the cleaning supplies sheet — a mapping layer, NOT copy.
 *
 * The strings live in `locales/{ar,en}.js` under `tasks.checklist.supplyContents`, per
 * I18N_STRATEGY.md. This module owns only the question "does this item have a contents line?",
 * which is a fact about the CleaningTemplate, not a translation.
 *
 * WHY A GATE AT ALL, rather than `t('tasks.checklist.supplyContents.' + itemId)` inline:
 * `useLanguage`'s `t()` echoes the KEY back when a translation is missing, so a bare lookup
 * would render `tasks.checklist.supplyContents.mineral_water` on screen for every supplies item
 * we have not written a hint for. Same guard, same reason, as `notificationLabels.js`.
 *
 * ⚠️ The keys are CleaningTemplate `itemId`s (`be/scripts/seed-cleaning-template.js`). They are
 * IMMUTABLE and are snapshotted onto every task, so a renamed one silently drops the hint rather
 * than failing the build.
 *
 * ── Why this is display-only ──────────────────────────────────────────────────────────────────
 * The contents are deliberately NOT template items. A template item is separately answerable and
 * blocks task completion until answered; the crew certifies the KIT, not each toothbrush. Adding
 * them as items would also need a new template version and would reshape every future task's
 * `supplies[]`. `config/amenityCatalogue.js` draws the other half of this boundary: consumables
 * are a cleaning-checklist concern, never a fact about the unit.
 */

/**
 * The items this build spells out. Anything else renders with no hint line.
 *
 * ⚠️ NOT SUPPLIES-ONLY ANY MORE (2026-09-07). `CleaningChecklistModal` calls this for every row in
 * both sheets, so a CHECKLIST item can carry a hint too — `guest_display_screen` does, because the
 * label can only hold the instruction and not what to do when the answer is no. The function and
 * locale key keep their `supply…` names: renaming them would churn the existing supplies keys for
 * no behavioural gain. Read the name as "item hint", not "supplies only".
 */
const ITEMS_WITH_CONTENTS = ['personal_care_amenities', 'guest_display_screen'];

/**
 * The contents line for a supplies item, or null when we do not spell that item out.
 *
 * @param {Function} t       from `useLanguage()`
 * @param {string}   itemId  CleaningTemplate item id
 */
export const supplyContents = (t, itemId) =>
  (ITEMS_WITH_CONTENTS.includes(itemId) ? t(`tasks.checklist.supplyContents.${itemId}`) : null);
